import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "AbroadJacks";
const SITE_URL = "https://abroadjacks.com";
const DEFAULT_IMAGE = `${SITE_URL}/broadjacks-logo.png`;
const SUPPORT_PHONE = "+91 9573070830";
const SUPPORT_EMAIL = "loganoverseas@gmail.com";

const DEFAULT_SEO = {
  title: "AbroadJacks | Study Abroad Consultancy Marketplace",
  description:
    "AbroadJacks is a study abroad consultancy marketplace where students can compare trusted consultancies and their latest offers in one platform.",
  keywords:
    "study abroad marketplace, overseas education consultancy, student visa support, study abroad offers, AbroadJacks",
  robots: "index,follow",
  canonicalPath: "/",
};

function getSeoForPath(pathname) {
  if (pathname === "/") {
    return {
      title: "AbroadJacks | Marketplace for Study Abroad Consultancies and Offers",
      description:
        "Discover, compare, and connect with trusted study abroad consultancies and exclusive offers on a single student-focused marketplace.",
      canonicalPath: "/",
      robots: "index,follow",
    };
  }

  if (pathname === "/consultancies") {
    return {
      title: "Study Abroad Consultancy Marketplace Listings | AbroadJacks",
      description:
        "Browse and compare verified study abroad consultancies, destination expertise, and active offers to find the right overseas education partner.",
      canonicalPath: "/consultancies",
      robots: "index,follow",
    };
  }

  if (pathname === "/about") {
    return {
      title: "About AbroadJacks | Our Story & Founder",
      description:
        "Learn how AbroadJacks helps students discover and compare study-abroad consultancies and learn about founder Akhil Varma, a Master's in Cyber Security graduate from Charles Sturt University, Australia.",
      canonicalPath: "/about",
      robots: "index,follow",
    };
  }

  if (pathname === "/contact") {
    return {
      title: "Contact AbroadJacks | Phone, WhatsApp & Email",
      description:
        "Contact AbroadJacks by phone, WhatsApp, or email for study abroad enquiries, consultancy listings, and platform support.",
      canonicalPath: "/contact",
      robots: "index,follow",
    };
  }

  if (pathname === "/blog/study-abroad-consultancy-marketplace") {
    return {
      title:
        "Study Abroad Consultancy Marketplace Guide for Students | AbroadJacks",
      description:
        "Learn how a study abroad consultancy marketplace helps students compare trusted consultancies and offers in one platform.",
      canonicalPath: "/blog/study-abroad-consultancy-marketplace",
      robots: "index,follow",
    };
  }

  if (pathname === "/blog/comparing-and-trust-abroadjacks") {
    return {
      title: "Comparing Study Abroad Platforms and Building Trust | AbroadJacks",
      description:
        "Learn why comparison and trust are essential when choosing a study abroad platform, and how AbroadJacks helps students make confident decisions.",
      canonicalPath: "/blog/comparing-and-trust-abroadjacks",
      robots: "index,follow",
    };
  }

  if (pathname === "/login") {
    return {
      title: "Login | AbroadJacks",
      description:
        "Log in to AbroadJacks to manage your marketplace profile, consultancy connections, and saved study abroad offers.",
      canonicalPath: "/login",
      robots: "noindex,nofollow",
    };
  }

  if (pathname === "/register") {
    return {
      title: "Create Account | AbroadJacks",
      description:
        "Create your AbroadJacks account to compare consultancies, save offers, and plan your study abroad journey from one marketplace.",
      canonicalPath: "/register",
      robots: "noindex,nofollow",
    };
  }

  if (pathname === "/forgot-password") {
    return {
      title: "Reset Password | AbroadJacks",
      description: "Reset your AbroadJacks account password securely.",
      canonicalPath: "/forgot-password",
      robots: "noindex,nofollow",
    };
  }

  if (pathname.startsWith("/consultancy/")) {
    return {
      title: "Consultancy Details | AbroadJacks",
      description:
        "View detailed consultancy information on AbroadJacks and connect with the right partner for your study abroad goals.",
      canonicalPath: pathname,
      robots: "noindex,nofollow",
    };
  }

  if (
    pathname.startsWith("/student/") ||
    pathname.startsWith("/owner") ||
    pathname.startsWith("/admin") ||
    pathname === "/wishlist"
  ) {
    return {
      title: `${SITE_NAME} Dashboard`,
      description:
        "Manage your AbroadJacks account, offers, preferences, and leads in your secure dashboard.",
      canonicalPath: pathname,
      robots: "noindex,nofollow",
    };
  }

  return {
    ...DEFAULT_SEO,
    canonicalPath: pathname,
  };
}

function upsertMeta(attr, key, content) {
  if (!content) return;

  let element = document.head.querySelector(`meta[${attr}='${key}']`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function upsertCanonical(href) {
  let element = document.head.querySelector("link[rel='canonical']");
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

function upsertJsonLd(payload) {
  const id = "abroadjacks-ld-json";
  let script = document.getElementById(id);

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(payload);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const routeSeo = getSeoForPath(location.pathname);
    const seo = { ...DEFAULT_SEO, ...routeSeo };
    const canonicalUrl = `${SITE_URL}${seo.canonicalPath || location.pathname}`;

    document.title = seo.title;

    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords || DEFAULT_SEO.keywords);
    upsertMeta("name", "robots", seo.robots || DEFAULT_SEO.robots);

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", DEFAULT_IMAGE);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);

    upsertCanonical(canonicalUrl);

    upsertJsonLd({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: DEFAULT_IMAGE,
      telephone: SUPPORT_PHONE,
      email: SUPPORT_EMAIL,
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: SUPPORT_PHONE,
          email: SUPPORT_EMAIL,
          contactType: "customer support",
          areaServed: "IN",
        },
      ],
      sameAs: [],
    });
  }, [location.pathname]);

  return null;
}
